import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchTenant, updateTenant, uploadTenantLogo } from '@/api/tenants';
import { queryKeys } from '@/lib/query-keys';
import type { UpdateTenantRequest, UploadTenantLogoRequest } from '@/types/settings';
import { applyTenantUpdate } from './tenant-session';

/**
 * `GET /api/tenants/:tenantId` — fila del negocio activo (nombre, logo).
 *
 * Ojo: la lista de negocios ya viene en `GET /api/auth/me` (store); esta query es para leer
 * la fila completa cuando una pantalla la necesita (y para tener su `updatedAt`).
 */

/** El `?? ''` de las funciones no llega a ejecutarse: sin tenant la query está deshabilitada. */
export function useTenant(tenantId: string | null) {
  return useQuery({
    queryKey: queryKeys.tenants.detail(tenantId ?? ''),
    queryFn: () => fetchTenant(tenantId ?? ''),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}

/**
 * `PATCH /api/tenants/:tenantId` (solo ADMIN).
 *
 * Al responder bien llama a `applyTenantUpdate`: el store y la caché de `/api/auth/me` se
 * actualizan para que el sidebar y el selector de negocio muestren el nombre/logo nuevos.
 */
export function useUpdateTenant(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateTenantRequest) => updateTenant(tenantId ?? '', payload),
    onSuccess: (data) => {
      if (tenantId) queryClient.setQueryData(queryKeys.tenants.detail(tenantId), data);
      applyTenantUpdate(data);
    },
  });
}

/**
 * `POST /api/tenants/:tenantId/logo` (solo ADMIN) — sube el archivo del logo.
 *
 * No toca la caché: el logo no queda guardado en el negocio hasta que el formulario se envía con
 * `PATCH /api/tenants/:tenantId` (lo hace `useUpdateTenant`). Es el mismo reparto que las fotos del
 * catálogo (`useUploadProductPhoto` + guardar el producto).
 */
export function useUploadTenantLogo(tenantId: string | null) {
  return useMutation({
    mutationFn: (payload: UploadTenantLogoRequest) => uploadTenantLogo(tenantId ?? '', payload),
  });
}
